// @flow

/**
 * Utilities that deal with currency, leveraging client-side Griffin 🎉
 */

import griffinNumber from 'griffin-paypal/number'
import { parseLocalizedNumberAsFloat } from '../../lib/amountUtils'
import getGriffinMetadata from './griffin-utils'
import log from 'loglevel'

/**
 * Get the supported currency pattern for specific style for the current locale
 * @param  {String} amount        the amount
 * @param  {String} currencyCode  the currency code
 * @param  {String} griffinPresetKey  griffin preset keys for various formats of output
 * @param  {number} maximumFractionDigits If we want to set the maximum fraction digits to zero or something else
 * @return {String}               the formatted amount
 */
export function formatAmount(
  amount: string,
  currencyCode: string,
  griffinPresetKey: string = 'CURRENCY_FORMAT_SYMBOLISOCURRENCY',
  maximumFractionDigits: ?number
) {
  if (!amount || !currencyCode) {
    log.warn('formatAmount() requires amount and currencyCode')
    return ''
  }

  const griffinMetadata = getGriffinMetadata()
  const griffinNumberHandler = griffinNumber.createHandler(griffinMetadata)
  const normalizedAmount = griffinNumberHandler.normalizeNumber(amount)
  let options = griffinNumberHandler[griffinPresetKey]
  if (maximumFractionDigits !== undefined) {
    options.maximumFractionDigits = maximumFractionDigits
  }
  return griffinNumberHandler.formatCurrency(
    {
      currency: currencyCode,
      value: normalizedAmount, // this argument only accepts floats or strings in US number format
    },
    options
  )
}

/**
 * Get the supported currency pattern for specific style for the current locale
 * without normalizing the data as canonical data is already normalized.
 * Use this method when you are getting the value from our services already
 * in cannonical form regardless of the currency or country.
 * Example: normalized (canonical): 50.00 EUR
 *          unnormalized: 50,00 EUR (requires normalizing before formatting)
 * @param  {String} amount        the amount
 * @param  {String} currencyCode  the currency code
 * @param  {String} griffinPresetKey  griffin preset keys for various formats of output
 * @return {String}               the formatted amount
 * @throws {Object}               TypeError from griffin
 */
export function formatCanonicalAmount({
  amount,
  currencyCode,
  griffinPresetKey = 'CURRENCY_FORMAT_SYMBOLISOCURRENCY',
}: {
  amount: string,
  currencyCode: string,
  griffinPresetKey: string,
}) {
  if (!amount || !currencyCode) {
    log.warn('formatCanonicalAmount() requires amount and currencyCode')
    return ''
  }

  const griffinMetadata = getGriffinMetadata()
  const griffinNumberHandler = griffinNumber.createHandler(griffinMetadata)
  // @throws TypeError
  return griffinNumberHandler.formatCurrency(
    {
      currency: currencyCode,
      value: amount, // this argument only accepts floats or strings in US number format
    },
    griffinNumberHandler[griffinPresetKey]
  )
}

/**
 * Get the amount in an ordered array of "parts" that you can assemble together
 * @param  {String} amount        the amount
 * @param  {String} currencyCode  the currency code
 * @param  {String} separator      decimal separator
 * @return {Array}                the amount parts
 */
export function getAmountParts(
  amount: string,
  currencyCode: string,
  separator: CurrencySeparator
) {
  const griffinMetadata = getGriffinMetadata()
  const griffinNumberHandler = griffinNumber.createHandler(griffinMetadata)

  const amountAsFloat = parseLocalizedNumberAsFloat(amount, separator)

  return griffinNumberHandler.formatCurrency(
    {
      currency: currencyCode,
      value: amountAsFloat,
    },
    griffinNumberHandler.CURRENCY_FORMAT_SYMBOLISOCURRENCY.parts()
  )
}

/**
 * Get sorted currency code list
 * @param  {Array<string>} currencyCodeList   the currency code list
 * @param  {Object} currencySymbolMap         the map with currency code and symbol
 * @return {Array<string>}                    the sorted currency code list
 */
export function sortCurrencyList(
  currencyCodeList: Array<string>,
  currencySymbolMap: Object
): Array<string> {
  let currencyList = []
  if (currencyCodeList && currencyCodeList.length) {
    currencyList = currencyCodeList
  } else {
    currencyList = Object.keys(currencySymbolMap)
  }

  return currencyList.sort()
}

export function showCurrencySymbol(
  amount: string,
  currencyCode: string,
  currencyDecimal: CurrencySeparator
) {
  const parts = getAmountParts(amount, currencyCode, currencyDecimal)

  return parts.find(part => part.type === 'symbol') || false
}
